import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';

/* ===========================================================================
 * StatusChip — a status triple with a mark on it, and the one chip in the system
 * that CANNOT be colour-only.
 *
 * §1 of docs/design-rules.md: "Each state is a triple (surface / border / ink),
 * and colour never carries meaning alone — always pair it with a word, a letter
 * or a dot." §6 restates it as an anti-pattern: do not colour a state without
 * also wording it.
 *
 * ---------------------------------------------------------------------------
 * THE RULE IS STRUCTURAL HERE, NOT DOCUMENTED.
 *
 * `Badge` already draws the triple correctly and it is a plain `<span>` with a
 * variant: `<Badge variant="danger" />` with no children compiles, renders an
 * 8px empty red rectangle, and means "absent" to whoever wrote it and nothing at
 * all to a reader in sunlight or a reader with a screen reader. That is not a
 * criticism of `Badge` — a tinted tag is a legitimate generic object, and it is
 * used for things that are not statuses. It is the reason this component exists
 * beside it: when the tint MEANS a state, the non-colour signal has to be
 * impossible to leave out.
 *
 * So the API admits exactly two shapes, as a union:
 *
 *   { tone, children }            a word. A dot is drawn beside it, or a letter
 *                                 if one is given.
 *   { tone, letter, label }       a letter alone — for a calendar cell or a
 *                                 dense grid where a word does not fit. `label`
 *                                 is then REQUIRED, because "P" announced on its
 *                                 own is exactly as meaningless to the ear as a
 *                                 bare colour is to the eye.
 *
 * There is no third shape. `<StatusChip tone="danger" />` does not typecheck,
 * and neither does a letter-only chip with no accessible name. That is the whole
 * point of the file — a rule enforced in review is a rule that ships broken on
 * the screen nobody reviewed.
 *
 * ---------------------------------------------------------------------------
 * THE MARK. A dot by default, a letter when one is passed.
 *
 * The dot is `bg-current`, i.e. the tone's own `-on-surface` ink, which is the
 * one colour in the triple already measured against the tint it sits on (AAA in
 * both themes). Drawing it in the SOLID hue instead — which the mocks do — would
 * put an unmeasured pair on screen for the sake of 6px.
 *
 * A letter is set in the same `text-label` as the word and never in caps at
 * 11px: it is a word of one character, and §1's ban is about size, not length.
 *
 * ---------------------------------------------------------------------------
 * INK. `-on-surface`, never `-foreground` (the ink meant for the SOLID fill) and
 * never the solid `-DEFAULT` itself. `-foreground` on a tint is white-on-pastel
 * in light and, because the dark scheme puts the on-colour and the container on
 * the same tone, literally invisible in dark. `Badge.tsx` carries the same note;
 * this is the mistake the token layer's two ink roles exist to prevent.
 *
 * TYPE is `text-label` (12.5px) at `font-bold`. The size is the rules file's
 * floor for a label and does not move; the WEIGHT is the mock's, which draws
 * every status chip at 700 rather than the 600 the §1 type table lists. 700 is
 * the change that makes a two-word status readable at a glance down a column of
 * them — it thickens the word without shrinking it, which is the opposite trade
 * from the 11px the mock also asks for and this file refuses.
 *
 * GEOMETRY is `min-height` plus padding, so an Arabic status that wraps grows
 * the chip rather than clipping it. 24px and 10px are the mock's numbers, near
 * enough: it draws the chip at 23–25px tall and 9–10px in from the edge
 * depending on the screen, and 24px is both the middle of that and the WCAG
 * 2.5.8 target floor, so the chip never lands under it on a screen where it
 * happens to be tappable.
 *
 * RTL: the mark and the word are flex children on the inline axis, so they swap
 * ends with `dir` on their own.
 * ======================================================================== */

/**
 * The five status families plus the teal accent. There is no `primary` tone: a
 * brand-tinted status would claim the one hue reserved for the active nav
 * marker, the primary action and the series meaning "on site" (rules §1).
 *
 * `neutral` is a real member and not a fallback. "No state to report" is a claim
 * in its own right in this product — a rest day is not a 0% day (rule 3.9), a
 * day with no record is not an absence (rule 3.4) — and it must not be drawn in
 * a hue that implies something went either well or badly.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'accent';

/**
 * The triple per tone, spelled out rather than composed. `border-warning-border`
 * is greppable; `` border-`${tone}`-border `` is not — and Tailwind scans source
 * text, so a composed class emits no CSS at all and the chip loses its edge
 * silently.
 */
const TONE_CLASS: Record<StatusTone, string> = {
  success: 'bg-success-surface border-success-border text-success-on-surface',
  warning: 'bg-warning-surface border-warning-border text-warning-on-surface',
  danger: 'bg-danger-surface border-danger-border text-danger-on-surface',
  info: 'bg-info-surface border-info-border text-info-on-surface',
  neutral: 'bg-neutral-surface border-neutral-border text-neutral-on-surface',
  accent: 'bg-accent-surface border-accent-border text-accent-on-surface',
};

interface StatusChipBase extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Which status family this is. See `StatusTone`. */
  tone: StatusTone;
  /**
   * A single character carried in place of the dot — the calendar's P / L / A.
   * Decorative when a word is present (the word is what gets announced);
   * load-bearing, and paired with a required `label`, when it stands alone.
   */
  letter?: string;
  /**
   * The accessible name, when the chip's visible content is not a readable word.
   * Rendered `sr-only`.
   */
  label?: string;
}

export type StatusChipProps =
  /** A word — the ordinary case. A dot is added unless a `letter` is given. */
  | (StatusChipBase & { children: ReactNode })
  /** A letter alone. `label` becomes mandatory; see the header note. */
  | (StatusChipBase & { children?: undefined; letter: string; label: string });

export const StatusChip = forwardRef<HTMLSpanElement, StatusChipProps>((props, ref) => {
  const { tone, letter, label, className, children, ...rest } = props;
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-xs border px-2.5 py-0.5 text-label font-bold',
        'min-h-6',
        TONE_CLASS[tone],
        className
      )}
      {...rest}
    >
      {letter != null ? (
        <span
          // Decorative only when a word is beside it. On its own it is the
          // visible content and `label` carries the announcement, so hiding it
          // from the reader either way keeps the two cases identical to the ear.
          aria-hidden="true"
          className="flex-none font-semibold tabular-nums"
        >
          {letter}
        </span>
      ) : (
        // The dot. `bg-current` is the tone's `-on-surface` ink — the one colour
        // in the triple already measured against this tint.
        <span aria-hidden="true" className="h-1.5 w-1.5 flex-none rounded-full bg-current" />
      )}
      {children}
      {label != null && <span className="sr-only">{label}</span>}
    </span>
  );
});
StatusChip.displayName = 'StatusChip';
